import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle, ArrowRight, LogIn, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../Logo';
import { toast } from 'sonner';

export const LoginPage = ({ onSwitch, onForgot }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const lastSubmitRef = useRef(0);

  const pendingPlan = (() => {
    try {
      const raw = sessionStorage.getItem('d3_pending_plan');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Please fill in all fields');
      return;
    }

    const now = Date.now();
    if (now - lastSubmitRef.current < 2000) return;
    lastSubmitRef.current = now;

    setIsLoading(true);
    try {
      const result = await login(email, password);
      const userData = result.user;

      // Backend is the source of truth for billing status
      // If billingRequired=true, App.js route guard will handle the redirect
      if (userData?.billingRequired) {
        sessionStorage.removeItem('d3_pending_plan');
        toast.success('Welcome back! Completing your billing setup...');
        window.history.pushState({}, '', '/billing/complete-setup');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        sessionStorage.removeItem('d3_pending_plan');
        toast.success('Welcome back!');
        window.history.pushState({}, '', '/dashboard');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  const handleDemoLogin = () => {
    // Redirect to the full demo experience on app.d3command.com
    window.location.href = 'https://app.d3command.com/demo';
  };

  return (
    <div className="min-h-screen bg-[#070b14] flex items-center justify-center px-4 dark" data-testid="login-page">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <div className="text-center mb-8 flex justify-center">
          <Logo size="large" />
        </div>

        {/* Pending plan banner */}
        {pendingPlan && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center gap-2"
            data-testid="pending-plan-banner"
          >
            <Sparkles className="w-4 h-4 text-indigo-400 flex-shrink-0" />
            <p className="text-sm text-indigo-300">
              Sign in to continue with the <strong className="text-indigo-200">{pendingPlan.planName}</strong> plan
            </p>
          </motion.div>
        )}

        {/* Form card */}
        <div className="p-8 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                data-testid="login-error"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </motion.div>
            )}

            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full h-11 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm text-gray-400">Password</label>
                <button
                  type="button"
                  onClick={onForgot}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full h-11 pl-11 pr-11 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10"></div>
            <span className="text-xs text-gray-500">or</span>
            <div className="flex-1 h-px bg-white/10"></div>
          </div>

          {/* Demo login - redirects to app.d3command.com */}
          <a
            href="https://app.d3command.com/demo"
            className="w-full h-11 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-all flex items-center justify-center gap-2 text-sm font-medium"
            data-testid="demo-login-btn"
          >
            <ArrowRight className="w-4 h-4" />
            Try Demo Account
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-semibold ml-1">
              DEMO
            </span>
          </a>
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          Don't have an account?{' '}
          <button onClick={onSwitch} className="text-blue-400 hover:text-blue-300 font-medium transition-colors" data-testid="switch-to-signup">
            Sign up
          </button>
        </p>
      </motion.div>
    </div>
  );
};
